import { alpha } from "@mui/material";
import type { Theme } from "@mui/material";

export function buildTableStyles(theme: Theme): string {
  return `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

    /* Reset de fundo para todos os containers do Handsontable */
    .custom-table-wrapper .handsontable,
    .custom-table-wrapper .htCore,
    .custom-table-wrapper .wtHider,
    .custom-table-wrapper .wtSpreader,
    .custom-table-wrapper .ht_clone_top,
    .custom-table-wrapper .ht_clone_left,
    .custom-table-wrapper .ht_clone_corner,
    .custom-table-wrapper .ht_master .wtHolder {
      background-color: ${theme.palette.background.card} !important;
      border: none !important;
    }

    .custom-table-wrapper {
      font-family: 'Inter', sans-serif;
      transition: background-color 0.3s ease;
    }

    .custom-table-wrapper .handsontable {
      border: none !important;
      overflow: hidden;
    }

    .ht_master .wtHider {
      border: none !important;
      color: ${theme.palette.text.primary} !important;
    }

    /* Estilo de célula inválida (com erro de validação) */
    .custom-table-wrapper .handsontable td.htInvalid {
      background-color: transparent !important;
      color: ${theme.palette.error.main} !important;
      border: 1px solid ${theme.palette.error.main} !important;
    }

    .custom-table-wrapper .htInvalid::after {
      display: none !important;
    }

    /* Ícone de erro posicionado no canto direito da célula */
    .cell-error-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 16px;
      height: 16px;
      background-color: ${theme.palette.error.main};
      color: #fff;
      border-radius: 50%;
      font-size: 10px;
      font-weight: 900;
      z-index: 5;
      pointer-events: auto;
    }

    /* Cabeçalho da tabela */
    .custom-table-wrapper .handsontable th {
      background-color: ${theme.palette.background.card};
      color: ${theme.palette.text.secondary};
      font-weight: 600;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      border-bottom: 2px solid ${theme.palette.divider};
      border-top: none;
      border-right: none !important;
      border-left: none !important;
      padding: 8px 5px;
      text-align: left;
      vertical-align: middle;
    }

    /* Células da tabela */
    .custom-table-wrapper .handsontable td {
      border-top: 1px solid ${theme.palette.divider} !important;
      border-left: none !important;
      border-right: none !important;
      border-bottom: 1px solid ${theme.palette.divider} !important;
      background-color: transparent !important;
      color: ${theme.palette.text.primary} !important;
      vertical-align: middle !important;
    }

    /* Linhas alternadas (zebra striping) */
    .custom-table-wrapper .handsontable tbody tr:nth-of-type(odd) td,
    .custom-table-wrapper .handsontable tbody tr:nth-of-type(odd) th {
      background-color: ${theme.palette.background.paper} !important;
    }

    .custom-table-wrapper .handsontable tbody tr:nth-of-type(even) td,
    .custom-table-wrapper .handsontable tbody tr:nth-of-type(even) th {
      background-color: ${theme.palette.background.card} !important;
    }

    /* Hover nas linhas */
    .custom-table-wrapper .handsontable tbody tr:hover td,
    .custom-table-wrapper .handsontable tbody tr:hover th {
      background-color: ${theme.palette.background.default} !important;
    }

    /* Dropdown e autocomplete */
    .htDropdownMenu, .htAutocomplete {
      background: ${theme.palette.background.paper};
      padding: 4px;
      border: 1px solid ${theme.palette.divider};
    }

    .htDropdownMenu table.htCore td, .htAutocomplete table.htCore td {
      background-color: ${theme.palette.background.paper} !important;
      color: ${theme.palette.text.primary} !important;
      border: none;
      border-radius: 4px;
      padding: 8px 12px;
    }

    /* Input de edição */
    .handsontableInput {
      background-color: ${theme.palette.background.paper} !important;
      color: ${theme.palette.text.primary} !important;
      font-family: 'Inter', sans-serif !important;
      font-size: 13px !important;
    }

    .handsontableInputHolder {
      background-color: ${theme.palette.background.paper} !important;
    }

    /* Destaque do cabeçalho ao selecionar coluna */
    .custom-table-wrapper .handsontable th.ht__highlight {
      background-color: ${theme.palette.background.card} !important;
      color: ${theme.palette.text.primary} !important;
      border-bottom: 2px solid ${theme.palette.primary.main} !important;
      transition: background-color 0.2s ease;
    }

    /* Remove borda superior duplicada */
    .custom-table-wrapper .handsontable,
    .custom-table-wrapper .handsontable thead,
    .custom-table-wrapper .handsontable thead tr:first-child th {
      border-top: none !important;
    }

    /* Célula desabilitada (ex: título quando tipo não é Host) */
    .custom-table-wrapper .handsontable td.disabled-cell {
      position: relative !important;
      cursor: not-allowed !important;
      color: ${theme.palette.text.disabled} !important;
    }

    .custom-table-wrapper .handsontable td.disabled-cell::after {
      content: '';
      position: absolute;
      inset: 0;
      background: repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 4px,
        ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"} 4px,
        ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0,0,0,0.1)"} 8px
      );
      pointer-events: none;
      z-index: 1;
    }

    .disabled-cell-label {
      font-size: 11px;
      font-style: italic;
      color: ${theme.palette.text.disabled};
      position: relative;
      z-index: 2;
    }

    /* Célula de Tags: chips leves em linha única */
    .custom-table-wrapper .handsontable td .em-tags-cell {
      display: flex;
      align-items: center;
      gap: 4px;
      height: 100%;
      flex-wrap: nowrap;
      overflow: hidden;
    }

    .custom-table-wrapper .handsontable td .em-tag-chip {
      display: inline-flex;
      align-items: center;
      max-width: 90px;
      padding: 1px 8px;
      border-radius: 10px;
      font-size: 11px;
      line-height: 18px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      background-color: ${theme.palette.action.selected};
      color: ${theme.palette.text.primary};
    }

    .custom-table-wrapper .handsontable td .em-tag-chip.em-tag-more {
      flex: none;
      color: ${theme.palette.text.secondary};
    }

    .custom-table-wrapper .handsontable td .em-tags-placeholder {
      font-size: 12px;
      font-style: italic;
      color: ${theme.palette.text.disabled};
    }

    /* Célula de cor: swatch arredondado mostrando a cor atual. O xadrez fica na
       base (indica transparência) e a cor é uma camada por cima — cores sólidas
       cobrem o xadrez (sem aparência de opacidade); cores com alpha deixam o
       xadrez aparecer. */
    .custom-table-wrapper .handsontable td .em-color-swatch {
      position: relative;
      width: 22px;
      height: 22px;
      border-radius: 6px;
      border: 1px solid ${alpha(theme.palette.text.primary, 0.25)};
      box-sizing: border-box;
      cursor: pointer;
      overflow: hidden;
      background-image:
        linear-gradient(45deg, ${alpha(theme.palette.text.primary, 0.18)} 25%, transparent 25%),
        linear-gradient(-45deg, ${alpha(theme.palette.text.primary, 0.18)} 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, ${alpha(theme.palette.text.primary, 0.18)} 75%),
        linear-gradient(-45deg, transparent 75%, ${alpha(theme.palette.text.primary, 0.18)} 75%);
      background-size: 8px 8px;
      background-position: 0 0, 0 4px, 4px -4px, -4px 0;
    }

    .custom-table-wrapper .handsontable td .em-color-swatch__fill {
      position: absolute;
      inset: 0;
    }

    .custom-table-wrapper .handsontable td .em-color-placeholder {
      font-size: 12px;
      font-style: italic;
      color: ${theme.palette.text.disabled};
    }

    /* Checkbox "selecionar todos" no cabeçalho da primeira coluna */
    .custom-table-wrapper .handsontable th .colHeader input.ht-select-all {
      cursor: pointer;
      margin: 0;
      vertical-align: middle;
      accent-color: ${theme.palette.primary.main};
    }
  `;
}
